import { Alert, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  Datagrid,
  FilterForm,
  ListContextProvider,
  Loading,
  ResourceContextProvider,
  SearchInput,
  TextField,
  Title,
  useList,
} from 'react-admin';
import {
  Else, If, Then, When,
} from 'react-if';

import type {
  AuthorizationAccountMember,
  AuthorizationAccountMemberRecord,
} from '@/Api/studioServerTypes';
import { authHttpClient } from '@/Auth/authProvider';

export default function AccountMemberPermissionsList(): JSX.Element {
  const {
    data: members = [],
    isLoading,
    isError,
  } = useQuery<AuthorizationAccountMember[]>({
    queryKey: ['authorization-account-members'],
    queryFn: async () => {
      const response = await authHttpClient('/api/authorization/account-members');

      return response.json as AuthorizationAccountMember[];
    },
  });
  const memberList = useList<AuthorizationAccountMemberRecord>({
    data: members.flatMap((member) => (member.ContactIdentifier ? [{
      ...member,
      id: member.ContactIdentifier,
      FullName: `${member.FirstName ?? ''} ${member.LastName ?? ''}`.trim(),
    }] : [])),
    isLoading,
    resource: 'authorization-account-members',
    sort: { field: 'FullName', order: 'ASC' },
  });

  const filters = [
    <SearchInput key='q' source='q' alwaysOn />,
  ];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
      <Title title='Permissions' />

      <When condition={isError}>
        <Alert severity='error' sx={{ mb: 2 }}>
          Unable to load account members
        </Alert>
      </When>

      <If condition={isLoading}>
        <Then>
          <Loading loadingPrimary='Loading account members' loadingSecondary='' />
        </Then>
        <Else>
          <ResourceContextProvider value='authorization-account-members'>
            <ListContextProvider value={memberList}>
              <FilterForm filters={filters} />
              <Datagrid
                rowClick={(id) => `/permissions/${String(id)}`}
                bulkActionButtons={false}
              >
                <TextField<AuthorizationAccountMemberRecord>
                  source='FullName'
                  label='Name'
                />
                <TextField<AuthorizationAccountMemberRecord>
                  source='Email'
                  label='Email'
                  emptyText='-'
                />
              </Datagrid>
            </ListContextProvider>
          </ResourceContextProvider>
        </Else>
      </If>
    </Box>
  );
}
